/**
 * Plugin Auth Service
 *
 * Manages authentication for plugin mode using the thin shell's
 * secure storage and localhost callback server for SSO.
 */

import { bridge } from '~/services/bridge'
import { clearPluginAccessToken, setPluginAccessToken } from '~/services/bridge/pluginAuth'
import { shell } from '~/services/bridge/thinShellClient'
import { extractApiErrorMessage } from '~/utils/shared'
import { getSSOLoginUrl } from '~/services/tenantService'

export interface PluginUser {
  id: string
  email: string
  name: string
  organizationId: string
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

export interface AuthStateResult {
  authenticated: boolean
  user?: PluginUser
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'curietech_access_token',
  REFRESH_TOKEN: 'curietech_refresh_token',
  EXPIRES_AT: 'curietech_expires_at',
  USER: 'curietech_user',
  AUTH_STATE: 'curietech_auth_state'
}

class PluginAuthService {
  private currentState: string | null = null
  private callbackListenerCleanup: (() => void) | null = null
  private authStateListeners: Array<(state: AuthStateResult) => void> = []

  getBaseUrl(): string {
    return bridge.getBaseUrl()
  }

  async loginWithPassword(
    email: string,
    password: string,
    mfaCode?: string
  ): Promise<{ success: boolean; error?: string; mfaRequired?: boolean }> {
    try {
      const baseUrl = this.getBaseUrl()
      const response = await fetch(`${baseUrl}/api/user/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Curietech-Plugin': 'true' },
        credentials: 'include',
        body: JSON.stringify({ email, password, mfa_code: mfaCode || null })
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: extractApiErrorMessage(data, 'Login failed') }
      }

      if (data.mfa_enabled && !mfaCode) {
        return { success: false, mfaRequired: true, error: 'MFA code required' }
      }

      const token = data.access_token || data.token
      if (!token) {
        return { success: false, error: 'No token received from server' }
      }

      const tokens: AuthTokens = {
        accessToken: token,
        refreshToken: data.refresh_token || '',
        expiresAt: Date.now() + 24 * 60 * 60 * 1000
      }
      await this.storeTokens(tokens)
      setPluginAccessToken(token)
      this.notifyAuthStateChanged({ authenticated: true })
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Login failed' }
    }
  }

  async startSSOAuthWithEmail(email?: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!email) {
        return { success: false, error: 'Email is required for SSO login' }
      }

      this.cleanupCallbackListener()

      let shellState: string
      try {
        const result = await shell.startAuthCallbackServer()
        const port = new URL(result.callbackUrl).port
        shellState = `${result.state}:${port}`
      } catch {
        return { success: false, error: 'Failed to start auth callback server' }
      }

      this.currentState = shellState
      await shell.setStorageItem(STORAGE_KEYS.AUTH_STATE, this.currentState)

      this.listenForCallback()

      const baseUrl = this.getBaseUrl()
      const redirectBaseUrl = window.CURIETECH_SERVER_BASE_URL || baseUrl
      const ssoResponse = await getSSOLoginUrl(email, {
        pluginState: this.currentState,
        redirectBaseUrl
      })
      if (ssoResponse.success && ssoResponse.login_url) {
        await shell.openExternal(ssoResponse.login_url)
        return { success: true }
      }

      this.cleanupCallbackListener()
      await shell.stopAuthCallbackServer()
      this.currentState = null
      return { success: false, error: ssoResponse.error || 'SSO is not configured for this domain' }
    } catch (error) {
      this.cleanupCallbackListener()
      await shell.stopAuthCallbackServer()
      this.currentState = null
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start SSO auth'
      }
    }
  }

  async cancelAuth(): Promise<void> {
    this.cleanupCallbackListener()
    await shell.stopAuthCallbackServer()
    this.currentState = null
    await shell.deleteStorageItem(STORAGE_KEYS.AUTH_STATE)
  }

  async signOut(): Promise<void> {
    this.cleanupCallbackListener()
    await shell.stopAuthCallbackServer()
    this.currentState = null

    await Promise.all([
      shell.deleteStorageItem(STORAGE_KEYS.ACCESS_TOKEN),
      shell.deleteStorageItem(STORAGE_KEYS.REFRESH_TOKEN),
      shell.deleteStorageItem(STORAGE_KEYS.EXPIRES_AT),
      shell.deleteStorageItem(STORAGE_KEYS.USER),
      shell.deleteStorageItem(STORAGE_KEYS.AUTH_STATE)
    ])

    clearPluginAccessToken()
    this.notifyAuthStateChanged({ authenticated: false })
  }

  async getAuthState(): Promise<AuthStateResult> {
    const accessToken = await shell.getStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
    if (!accessToken) {
      return { authenticated: false }
    }

    const expiresAtStr = await shell.getStorageItem(STORAGE_KEYS.EXPIRES_AT)
    if (expiresAtStr) {
      const expiresAt = parseInt(expiresAtStr, 10)
      if (Date.now() > expiresAt) {
        return { authenticated: false }
      }
    }

    let user: PluginUser | undefined
    const userJson = await shell.getStorageItem(STORAGE_KEYS.USER)
    if (userJson) {
      try {
        user = JSON.parse(userJson)
      } catch {
        // Invalid user JSON
      }
    }

    return { authenticated: true, user }
  }

  async getAccessToken(): Promise<string | null> {
    const state = await this.getAuthState()
    if (!state.authenticated) return null
    return shell.getStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
  }

  onAuthStateChanged(listener: (state: AuthStateResult) => void): () => void {
    this.authStateListeners.push(listener)
    return () => {
      const index = this.authStateListeners.indexOf(listener)
      if (index !== -1) this.authStateListeners.splice(index, 1)
    }
  }

  private listenForCallback(): void {
    this.cleanupCallbackListener()

    const handler = async (...args: unknown[]) => {
      const data = args[0] as Record<string, string> | undefined
      if (!data?.token) return

      const tokens: AuthTokens = {
        accessToken: data.token,
        refreshToken: '',
        expiresAt: Date.now() + 24 * 60 * 60 * 1000
      }
      await this.storeTokens(tokens)
      setPluginAccessToken(data.token)
      this.cleanupCallbackListener()
      this.currentState = null
      this.notifyAuthStateChanged({ authenticated: true })
    }

    bridge.on('authCallback', handler)
    this.callbackListenerCleanup = () => bridge.off('authCallback', handler)
  }

  private cleanupCallbackListener(): void {
    if (this.callbackListenerCleanup) {
      this.callbackListenerCleanup()
      this.callbackListenerCleanup = null
    }
  }

  private async storeTokens(tokens: AuthTokens): Promise<void> {
    await shell.setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken)
    if (tokens.refreshToken) {
      await shell.setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken)
    }
    if (tokens.expiresAt) {
      await shell.setStorageItem(STORAGE_KEYS.EXPIRES_AT, tokens.expiresAt.toString())
    }
  }

  private notifyAuthStateChanged(state: AuthStateResult): void {
    for (const listener of this.authStateListeners) {
      try {
        listener(state)
      } catch (error) {
        console.error('[PluginAuthService] Listener error:', error)
      }
    }
  }
}

export const pluginAuthService = new PluginAuthService()
